<template>
    <sui-segment-group style="max-width: 700px; margin: auto" raised>
        <sui-segment inverted style="text-align: center; font-size: 18px">
            <sui-icon name="whatsapp" /> Configuracion de WhatsApp (Green API)
        </sui-segment>

        <!-- Connection Status Banner -->
        <sui-message
            :class="connectionStatusClass"
            style="margin: 15px; text-align: center"
        >
            <sui-icon :name="connectionStatusIcon" />
            {{ connectionStatusMessage }}
        </sui-message>

        <sui-segment style="padding: 20px">
            <sui-form>
                <sui-form-field>
                    <label>ID Instance</label>
                    <div class="ui input">
                        <input
                            type="text"
                            placeholder="Ej: 1234567890"
                            v-model="config.id_instance"
                        />
                    </div>
                </sui-form-field>

                <sui-form-field>
                    <label>API Token</label>
                    <div class="ui action input">
                        <input
                            :type="showToken ? 'text' : 'password'"
                            placeholder="Tu API Token de Green API"
                            v-model="config.api_token"
                        />
                        <sui-button
                            type="button"
                            @click="showToken = !showToken"
                            :icon="showToken ? 'eye slash' : 'eye'"
                        />
                    </div>
                </sui-form-field>

                <sui-divider />

                <sui-form-field>
                    <label>WhatsApp para atencion humana</label>
                    <div class="ui input">
                        <input
                            type="tel"
                            placeholder="Ej: +52 33 3333 3333"
                            v-model="config.support_whatsapp"
                        />
                    </div>
                </sui-form-field>

                <sui-form-field>
                    <label>Telefono para atencion humana</label>
                    <div class="ui input">
                        <input
                            type="tel"
                            placeholder="Ej: +52 33 3333 3333"
                            v-model="config.support_phone"
                        />
                    </div>
                </sui-form-field>

                <sui-form-field>
                    <sui-checkbox
                        toggle
                        label="Agregar aviso de canal automatico a las notificaciones"
                        v-model="config.automated_notice_enabled"
                    />
                    <small class="help-text">
                        El aviso indica que este canal solo envia informacion y muestra los datos de atencion humana.
                    </small>
                </sui-form-field>

                <sui-form-field>
                    <sui-checkbox
                        toggle
                        label="Permitir notificaciones automaticas"
                        v-model="config.automated_notifications_enabled"
                    />
                    <small class="help-text">
                        Desactivalo para detener de inmediato los envios automaticos sin afectar la configuracion de Green API.
                    </small>
                </sui-form-field>

                <sui-form-field>
                    <label>Limite de notificaciones automaticas por 24 horas (tope)</label>
                    <div class="ui input">
                        <input
                            type="number"
                            min="1"
                            max="99"
                            v-model.number="config.daily_automated_limit"
                        />
                    </div>
                    <small v-if="warmupActive" class="help-text">
                        Hoy el envio real esta limitado a <strong>{{ effectiveDailyLimit }}</strong> mensajes por el calentamiento del numero.
                    </small>
                </sui-form-field>

                <sui-divider horizontal>Ritmo de envio y seguridad</sui-divider>

                <sui-message warning size="small">
                    <sui-icon name="shield" />
                    Para proteger tu numero de bloqueos, los mensajes automaticos se envian
                    <strong>despacio</strong> (uno cada {{ minDelaySec }}&ndash;{{ maxDelaySec }} s)
                    y solo entre las <strong>{{ formatHour(config.send_window_start_hour) }}</strong>
                    y las <strong>{{ formatHour(config.send_window_end_hour) }}</strong>.
                </sui-message>

                <sui-message v-if="warmupActive" positive size="small">
                    <sui-icon name="fire" />
                    Numero en calentamiento: dia {{ warmupDay }} &mdash; hoy puede enviar hasta
                    <strong>{{ effectiveDailyLimit }}</strong> mensajes. El limite sube solo cada dia
                    hasta llegar a tu tope.
                </sui-message>

                <div class="two-column-grid">
                    <sui-form-field>
                        <label>Pausa minima entre mensajes (seg)</label>
                        <div class="ui input">
                            <input type="number" min="1" max="300" v-model.number="minDelaySec" />
                        </div>
                    </sui-form-field>
                    <sui-form-field>
                        <label>Pausa maxima entre mensajes (seg)</label>
                        <div class="ui input">
                            <input type="number" min="1" max="300" v-model.number="maxDelaySec" />
                        </div>
                    </sui-form-field>
                </div>

                <div class="two-column-grid">
                    <sui-form-field>
                        <label>Enviar desde (hora)</label>
                        <div class="ui input">
                            <input type="number" min="0" max="23" v-model.number="config.send_window_start_hour" />
                        </div>
                    </sui-form-field>
                    <sui-form-field>
                        <label>Enviar hasta (hora)</label>
                        <div class="ui input">
                            <input type="number" min="0" max="23" v-model.number="config.send_window_end_hour" />
                        </div>
                    </sui-form-field>
                </div>

                <sui-message info size="small">
                    <sui-icon name="info circle" />
                    Obtén tus credenciales en
                    <a href="https://green-api.com" target="_blank">green-api.com</a>
                </sui-message>
            </sui-form>
        </sui-segment>

        <sui-segment style="display: flex; justify-content: space-between; padding: 15px">
            <sui-button
                @click="testConnection"
                :class="{ loading: testing }"
                :disabled="!config.id_instance || !config.api_token"
            >
                <sui-icon name="plug" /> Probar conexion
            </sui-button>

            <sui-button
                primary
                @click="saveConfig"
                :class="{ loading: saving }"
                :disabled="!config.id_instance || !config.api_token"
            >
                <sui-icon name="save" /> Guardar
            </sui-button>
        </sui-segment>

        <!-- Last Check Info -->
        <sui-segment v-if="lastCheck" style="padding: 10px; background: #f9f9f9">
            <small style="color: #666">
                <sui-icon name="clock outline" />
                Ultima verificacion: {{ formatDate(lastCheck) }}
            </small>
        </sui-segment>
    </sui-segment-group>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import API from '../../api/Api';

const toast = useToast();

const config = ref({
    id_instance: '',
    api_token: '',
    support_whatsapp: '',
    support_phone: '',
    automated_notice_enabled: true,
    automated_notifications_enabled: false,
    daily_automated_limit: 2,
    min_send_delay_ms: 8000,
    max_send_delay_ms: 25000,
    send_window_start_hour: 9,
    send_window_end_hour: 20,
});

// Computed from the backend, read-only display of the warm-up ramp.
const effectiveDailyLimit = ref(null);
const warmupDay = ref(null);

const warmupActive = computed(() =>
    warmupDay.value !== null
    && effectiveDailyLimit.value !== null
    && effectiveDailyLimit.value < config.value.daily_automated_limit
);

// Delays are stored in ms but edited in seconds.
const minDelaySec = computed({
    get: () => Math.round((config.value.min_send_delay_ms || 0) / 1000),
    set: (v) => { config.value.min_send_delay_ms = Math.max(1, Number(v) || 0) * 1000; },
});
const maxDelaySec = computed({
    get: () => Math.round((config.value.max_send_delay_ms || 0) / 1000),
    set: (v) => { config.value.max_send_delay_ms = Math.max(1, Number(v) || 0) * 1000; },
});

const formatHour = (h) => `${String(h ?? 0).padStart(2, '0')}:00`;

const showToken = ref(false);
const saving = ref(false);
const testing = ref(false);
const connectionStatus = ref('unknown');
const lastCheck = ref(null);

const connectionStatusClass = computed(() => {
    switch (connectionStatus.value) {
        case 'connected': return 'positive';
        case 'disconnected': return 'warning';
        case 'error': return 'negative';
        default: return 'info';
    }
});

const connectionStatusIcon = computed(() => {
    switch (connectionStatus.value) {
        case 'connected': return 'check circle';
        case 'disconnected': return 'warning sign';
        case 'error': return 'times circle';
        default: return 'question circle';
    }
});

const connectionStatusMessage = computed(() => {
    switch (connectionStatus.value) {
        case 'connected': return 'Conectado a WhatsApp';
        case 'disconnected': return 'Desconectado - Escanea el codigo QR en Green API';
        case 'error': return 'Error de conexion';
        default: return 'Estado desconocido - Prueba la conexion';
    }
});

const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('es-MX');
};

const loadConfig = async () => {
    try {
        const response = await API.getWhatsAppConfig();
        if (!response.error && response.data) {
            config.value.id_instance = response.data.id_instance || '';
            config.value.api_token = response.data.api_token || '';
            config.value.support_whatsapp = response.data.support_whatsapp || '';
            config.value.support_phone = response.data.support_phone || '';
            config.value.automated_notice_enabled = !!response.data.automated_notice_enabled;
            config.value.automated_notifications_enabled = !!response.data.automated_notifications_enabled;
            config.value.daily_automated_limit = response.data.daily_automated_limit ?? 2;
            config.value.min_send_delay_ms = response.data.min_send_delay_ms ?? 8000;
            config.value.max_send_delay_ms = response.data.max_send_delay_ms ?? 25000;
            config.value.send_window_start_hour = response.data.send_window_start_hour ?? 9;
            config.value.send_window_end_hour = response.data.send_window_end_hour ?? 20;
            effectiveDailyLimit.value = response.data.effective_daily_limit ?? null;
            warmupDay.value = response.data.warmup_day ?? null;
            connectionStatus.value = response.data.connection_status || 'unknown';
            lastCheck.value = response.data.last_connection_check;
        }
    } catch (error) {
        console.error('Error loading WhatsApp config:', error);
    }
};

const saveConfig = async () => {
    if (!config.value.id_instance || !config.value.api_token) {
        toast.error('Completa todos los campos');
        return;
    }

    saving.value = true;
    try {
        if (config.value.automated_notice_enabled && !config.value.support_whatsapp && !config.value.support_phone) {
            toast.error('Agrega un WhatsApp o telefono para la atencion humana.');
            return;
        }

        const response = await API.saveWhatsAppConfig(config.value);

        if (response.error) {
            toast.error(response.message_client || 'Error al guardar');
        } else {
            toast.success('Configuracion guardada');
            connectionStatus.value = 'unknown';
        }
    } catch (error) {
        console.error('Error saving config:', error);
        toast.error('Error al guardar la configuracion');
    } finally {
        saving.value = false;
    }
};

const testConnection = async () => {
    testing.value = true;
    try {
        const response = await API.testWhatsAppConnection();

        if (response.error) {
            toast.error(response.message_client || 'Error al probar conexion');
            connectionStatus.value = 'error';
        } else {
            connectionStatus.value = response.data.status;
            lastCheck.value = Math.floor(Date.now() / 1000);

            if (response.data.connected) {
                toast.success('Conexion exitosa');
            } else {
                toast.warning(response.data.message || 'No autorizado');
            }
        }
    } catch (error) {
        console.error('Error testing connection:', error);
        toast.error('Error al probar la conexion');
        connectionStatus.value = 'error';
    } finally {
        testing.value = false;
    }
};

onMounted(() => {
    loadConfig();
});
</script>

<style scoped>
.ui.action.input button {
    background: #f0f0f0;
}

.ui.message.info {
    font-size: 13px;
}

.ui.message a {
    color: #2185d0;
    text-decoration: underline;
}

.help-text {
    display: block;
    color: #666;
    margin-top: 6px;
}

.two-column-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}
</style>
